/* global google */

import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';

const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';

let imaSdkPromise = null;

const loadImaSdk = () => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('window is not available'));
    }

    if (window.google && window.google.ima) {
        return Promise.resolve(window.google.ima);
    }

    if (imaSdkPromise) {
        return imaSdkPromise;
    }

    imaSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = IMA_SDK_URL;
        script.async = true;

        script.onload = () => {
            if (window.google && window.google.ima) {
                resolve(window.google.ima);
            } else {
                reject(new Error('IMA SDK loaded without google.ima'));
            }
        };

        script.onerror = () => {
            imaSdkPromise = null;

            reject(new Error('Could not load IMA SDK'));
        };

        document.head.appendChild(script);
    });

    return imaSdkPromise;
};

const buildAdTagUrl = (adTagUrl) => {
    try {
        const url = new URL(adTagUrl, window.location.href);
        const currentUrl = window.location.href.split('#')[0];

        if (!url.searchParams.get('correlator')) {
            url.searchParams.set('correlator', Date.now().toString());
        }

        if (!url.searchParams.get('description_url')) {
            url.searchParams.set('description_url', currentUrl);
        }

        if (!url.searchParams.get('url')) {
            url.searchParams.set('url', currentUrl);
        }

        return url.toString();
    } catch (err) {
        return adTagUrl;
    }
};

const getAdSlotSize = () => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 760;
    const width = Math.min(Math.max(viewportWidth - 32, 280), 760);

    return {
        width,
        height: Math.round((width * 9) / 16),
    };
};

class VideoPrerollModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            status: 'loading',
        };

        this.adContainerRef = React.createRef();
        this.contentVideoRef = React.createRef();

        this.adsLoader = null;
        this.adsManager = null;
        this.adDisplayContainer = null;
        this.isFinished = false;
    }

    componentDidMount() {
        window.addEventListener('resize', this._resizeAd);

        this._requestAd();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._resizeAd);

        if (this.adsManager) {
            this.adsManager.destroy();

            this.adsManager = null;
        }
    }

    _requestAd = () => {
        loadImaSdk()
            .then(() => {
                const adContainer = this.adContainerRef.current;
                const contentVideo = this.contentVideoRef.current;

                if (!adContainer || !contentVideo) {
                    this._finish();

                    return;
                }

                google.ima.settings.setLocale('pt_br');

                this.adDisplayContainer = new google.ima.AdDisplayContainer(adContainer, contentVideo);
                this.adDisplayContainer.initialize();

                this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);

                this.adsLoader.addEventListener(
                    google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                    this._onAdsManagerLoaded,
                    false,
                );

                this.adsLoader.addEventListener(
                    google.ima.AdErrorEvent.Type.AD_ERROR,
                    this._onAdError,
                    false,
                );

                const slotSize = getAdSlotSize();
                const adsRequest = new google.ima.AdsRequest();

                adsRequest.adTagUrl = buildAdTagUrl(this.props.adTagUrl);
                adsRequest.linearAdSlotWidth = slotSize.width;
                adsRequest.linearAdSlotHeight = slotSize.height;
                adsRequest.nonLinearAdSlotWidth = slotSize.width;
                adsRequest.nonLinearAdSlotHeight = 90;

                this.adsLoader.requestAds(adsRequest);
            })
            .catch(this._onAdError);
    }

    _onAdsManagerLoaded = (adsManagerLoadedEvent) => {
        const adsRenderingSettings = new google.ima.AdsRenderingSettings();

        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

        this.adsManager = adsManagerLoadedEvent.getAdsManager(
            this.contentVideoRef.current,
            adsRenderingSettings,
        );

        this.adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, this._onAdError);

        [
            google.ima.AdEvent.Type.LOADED,
            google.ima.AdEvent.Type.STARTED,
            google.ima.AdEvent.Type.COMPLETE,
            google.ima.AdEvent.Type.SKIPPED,
            google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
            google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
        ].forEach((eventType) => {
            this.adsManager.addEventListener(eventType, this._onAdEvent);
        });

        try {
            const slotSize = getAdSlotSize();

            this.adsManager.init(slotSize.width, slotSize.height, google.ima.ViewMode.NORMAL);
            this.adsManager.start();
        } catch (err) {
            this._onAdError(err);
        }
    }

    _onAdEvent = (event) => {
        switch (event.type) {
            case google.ima.AdEvent.Type.LOADED:
                this.setState({ status: 'loaded' });

                break;

            case google.ima.AdEvent.Type.STARTED:
                this.setState({ status: 'playing' });

                break;

            case google.ima.AdEvent.Type.COMPLETE:
            case google.ima.AdEvent.Type.SKIPPED:
            case google.ima.AdEvent.Type.ALL_ADS_COMPLETED:
            case google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED:
                this._finish();

                break;

            default:
                break;
        }
    }

    _onAdError = (err) => {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[VideoPrerollModal] video preroll unavailable', err);
        }

        this._finish();
    }

    _resizeAd = () => {
        if (!this.adsManager) {
            return;
        }

        const slotSize = getAdSlotSize();

        this.adsManager.resize(slotSize.width, slotSize.height, google.ima.ViewMode.NORMAL);
    }

    _finish = () => {
        if (this.isFinished) {
            return;
        }

        this.isFinished = true;

        if (this.adsManager) {
            this.adsManager.destroy();

            this.adsManager = null;
        }

        this.props.onComplete();
    }

    render() {
        const statusText = this.state.status === 'playing' ? 'Em andamento' : 'Carregando';

        const modal = (
            <div className="VideoPrerollModal" role="dialog" aria-modal="true" aria-label="Publicidade em video">
                <div className="VideoPrerollModal__panel">
                    <div className="VideoPrerollModal__header">
                        <span>Publicidade</span>
                        <span>
                            {statusText}
                        </span>
                    </div>
                    <div className="VideoPrerollModal__stage">
                        <video
                            ref={this.contentVideoRef}
                            className="VideoPrerollModal__contentVideo"
                            playsInline
                            muted
                        />
                        <div ref={this.adContainerRef} className="VideoPrerollModal__adContainer" />
                    </div>
                </div>
            </div>
        );

        if (typeof document === 'undefined') {
            return modal;
        }

        return createPortal(modal, document.body);
    }
}

VideoPrerollModal.propTypes = exact({
    adTagUrl: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
});

export default VideoPrerollModal;
